import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Inbox, Image as ImageIcon, FileEdit, Sparkles } from "lucide-react";
import { SECTIONS } from "./sections";
import { useAuth } from "../auth/AuthContext";

export const AdminDashboard = () => {
  const { user } = useAuth();
  const [inbox, setInbox] = useState({ total: 0, unread: 0 });
  const [uploads, setUploads] = useState(0);
  const [content, setContent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [subs, ups, cnt] = await Promise.all([
          api.get("/admin/submissions"),
          api.get("/admin/uploads"),
          api.get("/admin/content"),
        ]);
        setInbox({ total: subs.data.total, unread: subs.data.unread });
        setUploads(ups.data.items?.length || 0);
        setContent(cnt.data);
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <div className="max-w-4xl" data-testid="admin-dashboard">
      <p className="sc-eyebrow">Welcome</p>
      <h1 className="font-serif text-4xl md:text-5xl text-ink mt-2">
        Hi {user?.name || "there"}.
      </h1>
      <p className="sc-body mt-3 max-w-lg">
        Edit any section of the website, view enquiries and manage images from here. Changes go live the moment you save.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10">
        <StatCard icon={Inbox} to="/admin/inbox" label="Enquiries" primary={inbox.total} sub={`${inbox.unread} unread`} testid="stat-inbox" />
        <StatCard icon={ImageIcon} to="/admin/media" label="Images" primary={uploads} sub={uploads === 1 ? "file" : "files"} testid="stat-media" />
        <StatCard icon={Sparkles} to="/admin/content/global" label="Content" primary={SECTIONS.length} sub="editable sections" testid="stat-content" />
      </div>

      <h2 className="font-serif text-2xl text-ink mt-14 mb-5">Edit content</h2>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SECTIONS.map((s) => (
          <li key={s.key}>
            <Link to={`/admin/content/${s.key}`}
                  className="block bg-white rounded-xl border border-blush-200/70 px-5 py-4 hover:border-magenta/40 hover:shadow-sm transition-all"
                  data-testid={`edit-${s.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-lg text-ink">{s.label}</p>
                  {s.hint && <p className="text-[13px] text-muteink mt-0.5">{s.hint}</p>}
                </div>
                <FileEdit size={16} className="text-muteink shrink-0 mt-1" />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const StatCard = ({ icon: Icon, to, label, primary, sub, testid }) => (
  <Link to={to} className="bg-white rounded-2xl border border-blush-200/70 p-5 hover:border-magenta/40 hover:shadow-sm transition-all block" data-testid={testid}>
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FBEDEF,#F4DCE0)" }}>
        <Icon size={16} className="text-magenta" />
      </span>
      <p className="text-[11px] uppercase tracking-[0.22em] text-muteink font-semibold">{label}</p>
    </div>
    <p className="font-serif text-3xl text-ink mt-3">{primary}</p>
    <p className="text-[13px] text-muteink font-body mt-0.5">{sub}</p>
  </Link>
);

export default AdminDashboard;
